import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { acceptBody, acceptLabel, openProposal, PROPOSAL_DECLINE, PROPOSAL_LINE,
         type DeloadProposalRow } from "../lib/deloadProposal";

/** F15 4.2 — the client's open deload proposal, above their week: one line, two buttons (`lib/deloadProposal.ts`). */
export function DeloadProposal({ clientId, thisMonday, onChanged }: {
  clientId: string;
  /** `YYYY-MM-DD`, the Monday of the week holding today. */
  thisMonday: string;
  /** Re-read the client after the deload is written. */
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<DeloadProposalRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reread, setReread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void supabase.from("coach_deload_proposals").select("week_start, reason, decided").eq("client_id", clientId)
      .then(({ data }) => { if (!cancelled) setRows((data ?? []) as DeloadProposalRow[]); });
    return () => { cancelled = true; };
  }, [clientId, reread]);
  const open = openProposal(rows, thisMonday);
  if (open === null) return null;

  async function accept(weekStart: string) {
    setBusy(true); setError(null);
    const { error: e } = await supabase.functions.invoke("apply-next-week", { body: acceptBody(clientId, weekStart) });
    setBusy(false);
    if (e) { setError("Could not write the deload."); return; }
    setReread((n) => n + 1);
    onChanged();
  }
  async function decline(weekStart: string) {
    setBusy(true); setError(null);
    const { error: e } = await supabase.from("coach_deload_proposals")
      .update({ decided: "declined", decided_at: new Date().toISOString() })
      .eq("client_id", clientId).eq("week_start", weekStart.slice(0, 10));
    setBusy(false);
    if (e) { setError("Could not save that."); return; }
    setReread((n) => n + 1);
  }

  return (
    <div className="deload-proposal" role="status" title={open.reason}>
      <span className="deload-proposal-line">{PROPOSAL_LINE}</span>
      <button type="button" className="ht-button ht-button-primary" disabled={busy}
              onClick={() => void accept(open.week_start)}>{acceptLabel(open.week_start, thisMonday)}</button>
      <button type="button" className="ht-button ht-button-quiet" disabled={busy}
              onClick={() => void decline(open.week_start)}>{PROPOSAL_DECLINE}</button>
      {error !== null && <span className="deload-proposal-error">{error}</span>}
    </div>
  );
}
